import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ForumThread {
  readonly threadId: string;
  readonly title: string;
  readonly category: string;
  readonly status: string;
  readonly replyCount: number;
  readonly lastActivityAt: string | null;
}

const CATEGORIES = ['', 'GENERAL', 'PRODUCT', 'SUPPORT', 'IDEAS', 'ANNOUNCEMENTS'];
const STATUSES = ['', 'OPEN', 'ANSWERED', 'LOCKED', 'ARCHIVED'];

/**
 * GR L27.06 — Forum (`/forum`). The discussion board listing `collab discussion`
 * threads with their category, status, reply count and last activity, so members
 * can find open questions. Filterable by category and status.
 */
export function GrForumPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<ForumThread[]>([]);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ForumThread[] }>(`/v1/collab/discussions${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, category, status]);

  return (
    <main aria-label="forum">
      <h1>{tr('gr27.forum.title')}</h1>
      <div className="filters">
        <label>
          {tr('gr27.forum.category')}
          <select aria-label="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || tr('gr27.forum.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('gr27.forum.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('gr27.forum.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('gr27.forum.loading')}</p> : (
        <>
          <p aria-label="thread-count">{tr('gr27.forum.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{tr('gr27.forum.empty')}</p> : (
            <table aria-label="forum-table">
              <thead><tr><th>{tr('gr27.forum.colTitle')}</th><th>{tr('gr27.forum.category')}</th><th>{tr('gr27.forum.status')}</th><th>{tr('gr27.forum.colReplies')}</th><th>{tr('gr27.forum.colLastActivity')}</th></tr></thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.threadId}>
                    <td>{t.title}</td><td>{t.category}</td><td>{t.status}</td>
                    <td>{t.replyCount}</td><td>{at(t.lastActivityAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
